import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  StyleSheet,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { SafeAreaView } from 'react-native-safe-area-context';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import type { RouteProp } from '@react-navigation/native';
import { useAuth } from '../../context/AuthContext';
import { useTheme } from '../../styles/ThemeProvider';
import { useCommonStyles } from '../../styles/useCommonStyles';
import type { RootStackParamList } from '../../types/navigation';

type Props = {
  navigation: NativeStackNavigationProp<RootStackParamList, 'EmailSignIn'>;
  route: RouteProp<RootStackParamList, 'EmailSignIn'>;
};

export default function EmailSignInScreen({ navigation, route }: Props) {
  const theme = useTheme();
  const commonStyles = useCommonStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notification, setNotification] = useState<string | null>(null);
  const { signIn, resetPassword } = useAuth();
  const passwordInputRef = useRef<TextInput>(null);

  const handleSignIn = async () => {
    if (!email || !password) {
      setError('Please fill in all fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await signIn(email, password);

      // Get the redirect path from route params and decode it
      const redirectPath = route.params?.redirect;
      if (redirectPath) {
        const decodedPath = decodeURIComponent(redirectPath);
        if (decodedPath !== '/sign-in' && decodedPath !== '/') {
          // Map paths to screen names
          if (decodedPath === '/add-item') {
            navigation.replace('Main', { screen: 'AddItem' } as any);
          } else {
            navigation.replace('Main', { screen: 'Tabs', params: { screen: 'Cache' } } as any);
          }
        } else {
          navigation.replace('Main', { screen: 'Tabs', params: { screen: 'Cache' } } as any);
        }
      } else {
        navigation.replace('Main', { screen: 'Tabs', params: { screen: 'Cache' } } as any);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!email) {
      setError('Please enter your email address first');
      return;
    }

    setLoading(true);
    setError(null);
    setNotification(null);

    try {
      await resetPassword(email);
      setNotification('Password reset instructions have been sent to your email.');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <SafeAreaView style={commonStyles.safeArea}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={[commonStyles.screenContainer, styles.formWrapper]}>
          <View style={commonStyles.form}>
            <TouchableOpacity
              onPress={() => navigation.goBack()}
              style={commonStyles.backButton}
            >
              <Ionicons name="arrow-back" size={24} color={theme.colors.textPrimary} />
              <Text style={commonStyles.backButtonText}>Back</Text>
            </TouchableOpacity>

            <Text style={commonStyles.screenTitle}>Sign in with Email</Text>
            <Text style={commonStyles.screenSubtitle}>Enter your credentials</Text>

            <View style={commonStyles.formField}>
              <Text style={commonStyles.inputLabel}>Email</Text>
              <TextInput
                style={[commonStyles.input, error ? commonStyles.inputError : null]}
                placeholder="you@example.com"
                placeholderTextColor={theme.colors.textTertiary}
                value={email}
                onChangeText={(text) => {
                  setEmail(text);
                  if (error) setError('');
                }}
                autoCapitalize="none"
                keyboardType="email-address"
                autoComplete="email"
                editable={!loading}
                returnKeyType="next"
                onSubmitEditing={() => passwordInputRef.current?.focus()}
              />
            </View>

            <View style={commonStyles.formField}>
              <Text style={commonStyles.inputLabel}>Password</Text>
              <TextInput
                ref={passwordInputRef}
                style={[commonStyles.input, error ? commonStyles.inputError : null]}
                placeholder="••••••••"
                placeholderTextColor={theme.colors.textTertiary}
                value={password}
                onChangeText={(text) => {
                  setPassword(text);
                  if (error) setError('');
                }}
                secureTextEntry
                autoComplete="current-password"
                editable={!loading}
                returnKeyType="done"
                onSubmitEditing={handleSignIn}
              />
            </View>

            {error && <Text style={commonStyles.formErrorText}>{error}</Text>}
            {notification && (
              <View style={commonStyles.notificationContainer}>
                <Text style={commonStyles.notificationText}>{notification}</Text>
              </View>
            )}

            <TouchableOpacity
              style={[commonStyles.primaryButton, loading && commonStyles.disabledButton]}
              onPress={handleSignIn}
              disabled={loading}
            >
              {loading ? (
                <ActivityIndicator color={theme.colors.backgroundPrimary} />
              ) : (
                <Text style={commonStyles.primaryButtonText}>Sign In</Text>
              )}
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.link}
              onPress={handleResetPassword}
              disabled={loading}
            >
              <Text style={commonStyles.linkText}>Forgot your password?</Text>
            </TouchableOpacity>

            <TouchableOpacity
              style={commonStyles.link}
              onPress={() => navigation.navigate('EmailSignUp')}
              disabled={loading}
            >
              <Text style={commonStyles.linkText}>
                Don't have an account? Sign up
              </Text>
            </TouchableOpacity>
          </View>
        </View>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  flex: {
    flex: 1,
  },
  formWrapper: {
    flex: 1,
    justifyContent: 'center',
  },
});
